import { z } from 'zod';
import {
  conversionAttributionCapturedAtNow,
  conversionAttributionSchema,
  isConversionAttributionLive,
  type ConversionAttribution,
} from '@galapagos/shared';
import {
  ensureAuthTeardownParticipant,
  type AuthTeardownParticipant,
} from '@/services/auth-teardown';
import { hasSession } from '@/services/session';

/**
 * Browser-side persistence for the conversion attribution pair (ADR-132).
 *
 * `localStorage`, not the tab-scoped session store: a member who opens a
 * campaign link, closes the tab, and registers later the same day is the case
 * this exists for. The value is two opaque vendor ids and a source enum —
 * never the URL, never anything about the member. The window is applied on
 * READ so an expired pair can never be attached; a corrupt value is removed.
 */
export const CONVERSION_ATTRIBUTION_STORAGE_KEY = 'galapagos.conversion_attribution.v1';

/**
 * Ownership is decided at capture, mirroring the mobile envelope: a pair that
 * arrived with a session live belongs to that session and dies with it; an
 * anonymous landing capture survives the next sign-in — including a stale
 * token's 401 mid-funnel, which must not erase the landing the member came from.
 */
const envelopeSchema = z
  .object({
    record: conversionAttributionSchema,
    capturedWithSession: z.boolean(),
  })
  .strict();
type ConversionAttributionEnvelope = z.infer<typeof envelopeSchema>;

// Every storage touch is guarded twice: the global is absent under `next build`
// (Node) and the accessor itself can throw in privacy modes. Either degrades to
// "no attribution", never to a crash (mirrors `services/session.ts`).
function removeEnvelope(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(CONVERSION_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Unreadable storage holds nothing we can act on.
  }
}

function readEnvelope(): ConversionAttributionEnvelope | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(CONVERSION_ATTRIBUTION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeEnvelope();
    return null;
  }
  const result = envelopeSchema.safeParse(parsed);
  if (!result.success) {
    removeEnvelope();
    return null;
  }
  return result.data;
}

function writeEnvelope(envelope: ConversionAttributionEnvelope): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONVERSION_ATTRIBUTION_STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // Storage refused the write; the landing simply goes unattributed.
  }
}

/**
 * Ownership follows `hasSession()`: a tab that holds credentials — including
 * one recovering its bearer from the refresh token after a reload — is signed
 * in, and a capture made there must not survive into another member's session
 * in the same tab. Whether those credentials turn out to be dead is only known
 * at teardown, which is where that case is decided.
 */
export function persistConversionAttribution(record: ConversionAttribution): void {
  writeEnvelope({ record, capturedWithSession: hasSession() });
}

/** The live pair, or `null` — an expired pair is removed on the way out. */
export function readConversionAttribution(
  now: string = conversionAttributionCapturedAtNow(),
): ConversionAttribution | null {
  const envelope = readEnvelope();
  if (envelope === null) return null;
  if (!isConversionAttributionLive(envelope.record, now)) {
    removeEnvelope();
    return null;
  }
  return envelope.record;
}

/** Session-boundary cleanup: drops a session-owned pair, keeps an anonymous one. */
export function clearConversionAttributionForSession(): void {
  const envelope = readEnvelope();
  if (envelope !== null && envelope.capturedWithSession) removeEnvelope();
}

export const conversionAttributionTeardownParticipant: AuthTeardownParticipant = {
  id: 'conversion-attribution',
  clear: (context) => {
    // A 401 that ends a session which never established an access token is a
    // refresh token that had already expired: nobody was signed in, so the
    // landing it stamped as session-owned was in truth anonymous and stays
    // for the registration that follows. A 401 after a live bearer, or an
    // explicit sign-out, ends a real session and takes its capture with it.
    if (context.reason === 'api-401' && context.accessTokenWasLive === false) return;
    clearConversionAttributionForSession();
  },
};

// Joins teardown at module load so no screen or shell has to remember it;
// idempotent on re-evaluation (the registration-flow-store idiom).
ensureAuthTeardownParticipant(conversionAttributionTeardownParticipant);
